import { Marked, type Tokens } from 'marked';

/**
 * AI summaries are derived from user-influenced meeting transcripts, so the
 * markdown they produce is treated as untrusted. We harden marked's renderer
 * to drop raw inline HTML tokens and reject dangerous URL schemes in links
 * and images. TinyMCE applies its own sanitization on load — this is the
 * boundary check before content leaves this utility.
 */
const DANGEROUS_HREF = /^\s*(javascript|data|vbscript|file):/i;

const escapeAttr = (value: string): string =>
	value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const safeMarked = new Marked({
	async: false,
	gfm: true,
	breaks: true,
});

safeMarked.use({
	renderer: {
		html() {
			return '';
		},
		link({ href, title, tokens }: Tokens.Link) {
			const safeHref = !href || DANGEROUS_HREF.test(href) ? '#' : href;
			const inner = this.parser.parseInline(tokens);
			const titleAttr = title ? ` title="${escapeAttr(title)}"` : '';
			return `<a href="${escapeAttr(safeHref)}"${titleAttr}>${inner}</a>`;
		},
		image({ href, title, text }: Tokens.Image) {
			if (!href || DANGEROUS_HREF.test(href)) return escapeAttr(text);
			const titleAttr = title ? ` title="${escapeAttr(title)}"` : '';
			return `<img src="${escapeAttr(href)}" alt="${escapeAttr(text)}"${titleAttr}>`;
		},
	},
});

/**
 * Converts AI-generated markdown into an HTML string suitable for loading
 * into a TinyMCE-backed RichContentDocumentEditor via
 * createContentStateWithHtmlStringValue.
 *
 * `breaks: true` is important: AI summaries often put line-by-line labeled
 * content ("Subject: ...", "Opening Hook: ...") on consecutive lines without
 * blank-line separators, and without it those collapse into a wall of
 * run-on text.
 *
 * Accepts either plain markdown or markdown the backend has pre-wrapped in
 * `<lev-content>`/`<p style="margin:0;padding:0;font-size:14px;">` blocks
 * (`content-notes/generate` returns that shape). Without unwrapping those
 * first, the strict `html()` renderer drops the whole payload and everything
 * collapses into one squashed paragraph in the editor.
 */
export function markdownToEditorHtml(input: string): string {
	if (!input) return '';
	const markdown = input
		.replace(/<\/?lev-content[^>]*>/gi, '')
		.replace(/<p[^>]*>/gi, '')
		.replace(/<\/p>/gi, '\n')
		.replace(/&nbsp;/gi, ' ');
	return safeMarked.parse(markdown) as string;
}
